import React, { useState, useEffect } from "react";
// api gére les requetes vers l'api avec axios et la variable d'environnement;
import api from "../../api";
import { useAuth } from "../../provider/AuthProvider";
import UseTitle from "../../components/useTitle";
import "./CharacterUsedXP.scss";

const CharacterUsedXP = ({ character, setIsUsedXP, setRefresh }) => {
  // Définition du titre de la page
  UseTitle("Utilisation des points d'experience - " + character.name);

  const { isAdmin } = useAuth();

  const [nbXPUsed, setNbPointsXP] = useState(0);
  const [nbXP] = useState(character.xp);

  const [updatedSkills, setUpdatedSkills] = useState({});
  const [updatedSpells, setUpdatedSpells] = useState({});
  const [attributesUpdated, setAttributesUpdated] = useState({});

  const [isLoading, setIsLoading] = useState(false);
  const [messageError, setMessageError] = useState();

  // Récupération des jobs du personnage
  const jobs = character.jobs;
  // Regroupement des jobs par sources (region, parent, commun, all)
  const groupedJobs = jobs.reduce((groupedBySource, job) => {
    const source = job.source;
    // Création du groupe s'il n'existe pas
    if (!groupedBySource[source]) {
      groupedBySource[source] = [];
    }
    // Ajout du job au groupe
    groupedBySource[source].push(job);
    // console.log(groupedBySource);
    return groupedBySource;
  }, {});
  // Tri des jobs par id dans chaque groupe
  for (const source in groupedJobs) {
    groupedJobs[source].sort((a, b) => a.id - b.id);
  }

  // Récupération de la valeur temporaire du skill / spell
  const getUpdatedValue = (type, item) => {
    const state = type === "skill" ? updatedSkills : updatedSpells;
    return item.value + state[item.id] || 0;
  };

  // Calcul du coût en XP pour améliorer un skill / spell
  const xpCost = (type, item) => {
    const actualValue = getUpdatedValue(type, item);
    return actualValue < 75 ? 1 : 2;
  };

  // Calcul du coût en XP et du nombre de points
  const getXpCostAndValueGain = (actualValue, value) => {
    if (actualValue < 25) {
      return { xpCost: 1 * value, nbPoints: 2 * value };
      // Cas particulier pour redescendre en dessous de 25
    } else if (actualValue === 25) {
      return { xpCost: 0 * value, nbPoints: 1 * value };
    } else if (actualValue < 75) {
      return { xpCost: 1 * value, nbPoints: 1 * value };
      // Cas particulier pour redescendre en dessous de 75
    } else if (actualValue === 75 && value === -1) {
      return { xpCost: 1 * value, nbPoints: 1 * value };
    } else {
      return { xpCost: 2 * value, nbPoints: 1 * value };
    }
  };

  // Update des attributs en fonction des skills / spells améliorés
  const updateAttributes = () => {
    const calculAttributes = {};

    // Calcule de des bonus des attributs pour les skills améliorés
    Object.entries(updatedSkills).forEach(([skillId, value]) => {
      // Récupération du skill
      const skill = character.jobs
        .flatMap((job) => job.skills)
        .find((skill) => skill.id == skillId);

      if (skill) {
        // Calcul du bonus
        const bonus = Math.floor(value / 2);
        skill.attributs.forEach((attribute) => {
          // Récupération de l'attribut du character
          const attributeCharacter = character.attributes.find(
            (attr) => attr.attribute_code === attribute.name
          );
          if (!attributeCharacter) return;

          // Calcul du bonus de l'attribut
          const attributeBonus =
            (calculAttributes[attribute.name] || 0) + bonus;
          // Calcule du bonus maximal de l'attribut
          const attributeBonusMax =
            attributeCharacter.max_value - attributeCharacter.current_value;

          // Mise à jour de l'attribut sans dépasser la valeur maximale
          calculAttributes[attribute.name] = Math.min(
            attributeBonus,
            attributeBonusMax
          );
        });
      }
    });

    // Calcule de des bonus des attributs pour les spells améliorés
    Object.entries(updatedSpells).forEach(([spellId, value]) => {
      // Récupération du spell
      const spell = character.jobs
        .flatMap((job) => job.spells)
        .find((spell) => spell.id == spellId);

      if (spell) {
        // Calcul du bonus
        const bonus = Math.floor(value / 2);
        spell.attributs.forEach((attribute) => {
          // Récupération de l'attribut du character
          const attributeCharacter = character.attributes.find(
            (attr) => attr.attribute_code === attribute.name
          );
          if (!attributeCharacter) return;

          // Calcul du bonus de l'attribut
          const attributeBonus =
            (calculAttributes[attribute.name] || 0) + bonus;
          // Calcule du bonus maximal de l'attribut
          const attributeBonusMax =
            attributeCharacter.max_value - attributeCharacter.current_value;

          // Mise à jour de l'attribut sans dépasser la valeur maximale
          calculAttributes[attribute.name] = Math.min(
            attributeBonus,
            attributeBonusMax
          );
        });
      }
    });

    setAttributesUpdated(calculAttributes);
  };

  // Update du skill / spell
  const handleUpdate = (type, item, value) => {
    // Gestion en fonction du type de l'item (skill / spell)
    const state = type === "skill" ? updatedSkills : updatedSpells;
    const setState = type === "skill" ? setUpdatedSkills : setUpdatedSpells;

    // Récupération de la valeur temporaire de l'item
    const currentUpdatedValue = state[item.id] || 0;
    const actualValue = item.value + currentUpdatedValue;

    // Recupération du coût en XP et du nombre de points
    const { xpCost, nbPoints } = getXpCostAndValueGain(actualValue, value);

    // Vérification si on ajoute ou retire des points
    if (value === 1) {
      // Vérification si on a suffisamment d'XP et si on n'est pas au maximum
      if (nbXPUsed + xpCost > nbXP || actualValue >= item.value_max) return;
    } else if (value === -1) {
      // Vérification si on est au miximum
      if (currentUpdatedValue === 0) return;
    }

    // Mise à jour des valeurs
    setState({
      ...state,
      [item.id]:
        currentUpdatedValue + nbPoints < 0 ? 0 : currentUpdatedValue + nbPoints,
    });

    // Mise à jour du nombre d'XP utilisé
    setNbPointsXP(nbXPUsed + xpCost);
  };

  // Réinitialisation des valeurs
  const handleReset = () => {
    setUpdatedSkills({});
    setUpdatedSpells({});
    setAttributesUpdated({});
    setNbPointsXP(0);
  };

  // Vérification de la validité du personnage
  const checkUpdate = () => {
    setMessageError();

    // Vérification si au moins un skill ou un spell est amélioré
    if (
      Object.keys(updatedSkills).length === 0 &&
      Object.keys(updatedSpells).length === 0
    ) {
      setMessageError("Veuillez améliorer au moins un skill ou un spell.");
      return false;
    }

    // Vérification si on ne dépasse pas le nombre de points de XP utilisé
    if (nbXPUsed > nbXP) {
      setMessageError("Vous avez utilisé trop d'XP.");
      return false;
    }

    return true;
  };

  // Mise à jour du personnage
  const handleUpdateCharacter = () => {
    if (!checkUpdate()) {
      return;
    }
    setIsLoading(true);
    api
      .post(`/usedxp/`, {
        updatedSkills,
        updatedSpells,
        characterId: character.id,
      })
      .then(() => {
        setRefresh(true);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });

    setIsLoading(false);
  };

  // Mise à jour des attributs en fonction des skills / spells
  useEffect(() => {
    updateAttributes();
  }, [updatedSkills, updatedSpells]);

  return (
    <>
      <div className="character-used-xp">
        {isLoading ? (
          <div className="loading-data">
            <div className="loading-spinner"></div>
            <div className="loading-text">Chargement...</div>
          </div>
        ) : (
          <>
            {/* Header du personnage */}
            <div className="header">
              <h1>Utilisation XP : {character.name}</h1>
              <p>
                XP : {nbXP - nbXPUsed} / {nbXP}
              </p>
            </div>

            {/* Affichage des attributs */}
            <section className="character-section">
              <h2>Attributs</h2>
              <div className="attributes">
                {character.attributes.map((attribute) => {
                  const updatedValue =
                    attributesUpdated[attribute.attribute_code] || 0;
                  return (
                    <div className="attribute" key={attribute.attribute_code}>
                      <span className="attribute-name">
                        <strong>{attribute.attribute}</strong> (
                        {attribute.attribute_code})
                      </span>
                      <span className="attribute-value">
                        <strong>
                          {attribute.current_value + updatedValue}
                        </strong>
                        <small>/{attribute.max_value}</small>{" "}
                        {updatedValue > 0 && (
                          <span>
                            {" "}
                            <strong>+ {updatedValue}</strong>
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Métiers par source */}
            <section className="character-section">
              <h2>Métiers</h2>
              {["region", "parent", "commun", "all"].map(
                (source) =>
                  groupedJobs[source] && (
                    <div key={source} className="job-group">
                      <h3>{source}</h3>
                      <div className="jobs">
                        {groupedJobs[source].map((job) => (
                          // Affichage des jobs
                          <article className="job" key={job.id}>
                            <h4>{job.name}</h4>

                            {/* Affichage des skills */}
                            {job.skills.length > 0 && (
                              <div className="skills">
                                <h5>Compétences ({job.skills.length})</h5>
                                <ul>
                                  {job.skills.map((skill) => (
                                    // Affichage du skill
                                    <li key={skill.id} className="skill">
                                      <div className="name">
                                        {skill.name} (
                                        {skill.attributs
                                          .map((attr) => attr.name)
                                          .join(", ")}
                                        )
                                      </div>
                                      <div className="update">
                                        <div className="value">
                                          <span>
                                            <strong>
                                              {skill.value +
                                                (updatedSkills[skill.id] || 0)}
                                            </strong>
                                            <small>/{skill.value_max}</small>
                                          </span>
                                          {(updatedSkills[skill.id] || 0) >
                                            0 && (
                                            <strong>
                                              <span>
                                                {" "}
                                                + {updatedSkills[skill.id] || 0}
                                              </span>
                                            </strong>
                                          )}
                                        </div>

                                        {/* Affichage des boutons si skill pas au maximum */}
                                        {skill.value < skill.value_max && (
                                          <div className="buttons">
                                            <button
                                              className={
                                                !updatedSkills[skill.id] ||
                                                updatedSkills[skill.id] === 0
                                                  ? "bt disabled"
                                                  : "bt"
                                              }
                                              onClick={() =>
                                                handleUpdate("skill", skill, -1)
                                              }
                                              disabled={
                                                !updatedSkills[skill.id] ||
                                                updatedSkills[skill.id] === 0
                                              }
                                            >
                                              -
                                            </button>
                                            <button
                                              className={
                                                xpCost("skill", skill) +
                                                  nbXPUsed >
                                                  nbXP ||
                                                getUpdatedValue(
                                                  "skill",
                                                  skill
                                                ) >= skill.value_max
                                                  ? "bt disabled"
                                                  : "bt"
                                              }
                                              onClick={() =>
                                                handleUpdate("skill", skill, 1)
                                              }
                                              disabled={
                                                xpCost("skill", skill) +
                                                  nbXPUsed >
                                                  nbXP ||
                                                getUpdatedValue(
                                                  "skill",
                                                  skill
                                                ) >= skill.value_max
                                              }
                                            >
                                              +
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Affichage des spells */}
                            {job.spells.length > 0 && (
                              <div className="spells">
                                <h5>Sorts ({job.spells.length})</h5>
                                <ul>
                                  {job.spells.map((spell) => (
                                    // Affichage du spell
                                    <li key={spell.id} className="spell">
                                      <div className="infos">
                                        {spell.name} (
                                        {spell.attributs
                                          .map((attr) => attr.name)
                                          .join(", ")}
                                        )
                                      </div>
                                      <div className="update">
                                        <div className="value">
                                          <span>
                                            {spell.value +
                                              (updatedSpells[spell.id] || 0)}
                                            /{spell.value_max}
                                          </span>
                                          {(updatedSpells[spell.id] || 0) >
                                            0 && (
                                            <strong>
                                              <span>
                                                {" "}
                                                + {updatedSpells[spell.id] || 0}
                                              </span>
                                            </strong>
                                          )}
                                        </div>

                                        {/* Affichage des boutons si spell pas au maximum */}
                                        {spell.value < spell.value_max && (
                                          <div className="buttons">
                                            <button
                                              className={
                                                !updatedSpells[spell.id] ||
                                                updatedSpells[spell.id] === 0
                                                  ? "bt disabled"
                                                  : "bt"
                                              }
                                              onClick={() =>
                                                handleUpdate("spell", spell, -1)
                                              }
                                              disabled={
                                                !updatedSpells[spell.id] ||
                                                updatedSpells[spell.id] === 0
                                              }
                                            >
                                              -
                                            </button>
                                            <button
                                              className={
                                                xpCost("spell", spell) +
                                                  nbXPUsed >
                                                  nbXP ||
                                                getUpdatedValue(
                                                  "spell",
                                                  spell
                                                ) >= spell.value_max
                                                  ? "bt disabled"
                                                  : "bt"
                                              }
                                              onClick={() =>
                                                handleUpdate("spell", spell, 1)
                                              }
                                              disabled={
                                                xpCost("spell", spell) +
                                                  nbXPUsed >
                                                  nbXP ||
                                                getUpdatedValue(
                                                  "spell",
                                                  spell
                                                ) >= spell.value_max
                                              }
                                            >
                                              +
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </article>
                        ))}
                      </div>
                    </div>
                  )
              )}
            </section>

            {/* Boutons */}
            <div className="buttons">
              <button
                className={nbXPUsed === 0 ? "bt disabled" : "bt"}
                onClick={handleUpdateCharacter}
                disabled={nbXPUsed === 0}
              >
                Valider
              </button>
              <button
                className={nbXPUsed === 0 ? "bt disabled" : "bt"}
                onClick={() => handleReset()}
                disabled={nbXPUsed === 0}
              >
                Réinitialiser
              </button>
              <button className="bt" onClick={() => setIsUsedXP(false)}>
                Annuler
              </button>
            </div>
            {messageError && <p>{messageError}</p>}
          </>
        )}
      </div>
    </>
  );
};

export default CharacterUsedXP;
